<script setup>
/* global axios */
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import {
  useEventListener,
  useIntervalFn,
  useDocumentVisibility,
} from '@vueuse/core';
import GameCharacter from './GameCharacter.vue';
import { useGameAppearance } from './useGameAppearance';
import { characterProgress } from './characterEvolution';
const props = defineProps({
  accountId: { type: [String, Number], default: null },
  userId: { type: [String, Number], default: null },
});
const appearance = useGameAppearance(
  () => props.accountId,
  () => props.userId
);
const journey = ref(null);
const visibility = useDocumentVisibility();
let version = 0;
const load = async (reset = false) => {
  version += 1;
  const request = version;
  if (reset) journey.value = null;
  if (!props.accountId || !props.userId) return;
  const base = `/api/v1/accounts/${props.accountId}/talkvia_game_seasons`;
  try {
    const response = await axios.get(base, {
      params: { view: 'participant', companion: true },
    });
    if (request === version) journey.value = response.data.journey || null;
  } catch {
    if (request === version) journey.value = null;
  }
};
watch([() => props.accountId, () => props.userId], () => load(true), {
  immediate: true,
});
const { pause } = useIntervalFn(() => {
  if (visibility.value === 'visible') load();
}, 120000);
watch(visibility, (value, previous) => {
  if (value === 'visible' && previous === 'hidden') load();
});
const stopProgressListener = useEventListener(
  window,
  'talkvia:game-progress',
  event => {
    if (String(event.detail?.accountId) === String(props.accountId)) load();
  }
);
onBeforeUnmount(() => {
  stopProgressListener();
  pause();
  version += 1;
});
const evolution = computed(() =>
  characterProgress(journey.value?.player.xp, appearance.value.character)
);
const label = computed(
  () =>
    `${evolution.value.characterName} · ${evolution.value.name} · Nível ${evolution.value.level}. Abrir conquistas.`
);
</script>

<template>
  <RouterLink
    v-if="journey"
    :to="{
      name: 'talkvia_gamification',
      params: { accountId },
      query: { season: journey.season.id },
    }"
    class="relative flex shrink-0 flex-col items-center rounded-xl border border-n-weak bg-n-alpha-1 p-0.5 text-n-slate-12 focus-visible:outline-n-brand"
    :title="label"
    :aria-label="label"
  >
    <GameCharacter
      :key="`${accountId}:${userId}:${journey.season.id}`"
      :xp="journey.player.xp"
      :character="appearance.character"
      :motion="appearance.motion"
      :accessory="appearance.accessory"
      compact
    />
    <span
      class="absolute bottom-0 right-0 rounded bg-n-solid-1 px-1 text-[9px] font-bold leading-3"
      >{{ evolution.level }}</span
    >
    <progress
      :value="evolution.progress"
      max="100"
      :aria-label="`${evolution.remaining} XP para o próximo nível`"
      class="h-0.5 w-7 overflow-hidden rounded bg-n-alpha-2 [accent-color:currentColor]"
    />
  </RouterLink>
</template>
